import type { JournalPaneGuard } from './JournalPaneGuard';
import type { ConfirmationPress, TwoClickConfirmation } from '../utils/twoClickConfirmation';

/** What the runner needs from the task pane. */
export interface JournalActionHost {
  /**
   * Auth for the action's requests, checked against whoever the Journal tab was read for. False, after
   * the pane has said why, when the action must not go out.
   */
  prepare(): Promise<boolean>;
  /** Another journal action begins, so a Push warning shown before it no longer describes the next click. */
  actionStarting(): void;
  /** An action began (true) or ended (false): what could change its target is held meanwhile. */
  running(running: boolean): void;
}

export interface JournalActionOptions {
  /** The click may confirm a warning an earlier click showed, so starting it leaves that warning armed. */
  confirms?: boolean;
}

/**
 * How every journal button runs: one at a time, as whoever the Journal tab was read for, and with what
 * went wrong shown. A handler that sends requests outside it skips both checks.
 */
export class JournalActionRunner {
  constructor(private readonly guard: Pick<JournalPaneGuard, 'run' | 'busy'>, private readonly host: JournalActionHost) {}

  run(action: () => Promise<void>, failed: (error: unknown) => void | Promise<void>, options: JournalActionOptions = {}): Promise<void> {
    return this.guard.run(async () => {
      this.host.running(true);
      if (!options.confirms) this.host.actionStarting();
      try {
        if (!await this.host.prepare()) return;
        await action();
      } catch (error) {
        await this.report(failed, error);
      } finally {
        this.host.running(false);
      }
    });
  }

  /**
   * A click that may confirm a warning an earlier click showed. It is timed before anything else, the
   * busy check included, so a click made while the one before was still working counts as made then.
   * `starting` runs at once for a click that will run, to show it was taken.
   */
  runConfirming(
    confirmation: Pick<TwoClickConfirmation, 'pressed'>,
    action: (press: ConfirmationPress) => Promise<void>,
    failed: (error: unknown) => void | Promise<void>,
    starting?: () => void,
  ): Promise<void> {
    const press = confirmation.pressed();
    if (!this.guard.busy) starting?.();
    return this.run(() => action(press), failed, { confirms: true });
  }

  private async report(failed: (error: unknown) => void | Promise<void>, error: unknown): Promise<void> {
    try {
      await failed(error);
    } catch (reportError) {
      console.error('Failed to report a journal error:', reportError);
    }
  }
}
